import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, UserPlus, Mail, Shield, Trash2, Loader2, Copy, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_COLORS = {
  owner: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  editor: 'bg-green-100 text-green-700',
  viewer: 'bg-slate-100 text-slate-700'
};

const ROLE_DESCRIPTIONS = {
  viewer: 'Can view all data but cannot make changes',
  editor: 'Can add, edit, and delete transactions',
  admin: 'Full access including team management'
};

export default function TeamPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [lastMagicLink, setLastMagicLink] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/team/members`);
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setInviting(true);
    try {
      const response = await axios.post(`${API}/team/invite`, {
        email: inviteEmail,
        role: inviteRole
      });
      
      setLastMagicLink(window.location.origin + response.data.magic_link);
      toast.success('Invitation link created!');
      setInviteEmail('');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/team/members/${memberId}?role=${newRole}`);
      toast.success('Role updated');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId, email) => {
    if (!window.confirm(`Remove ${email} from the team?`)) return;
    
    try {
      await axios.delete(`${API}/team/members/${memberId}`);
      toast.success('Team member removed');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const copyMagicLink = () => {
    navigator.clipboard.writeText(lastMagicLink);
    toast.success('Link copied to clipboard');
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Team Management
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Invite partners and manage access permissions
            </p>
          </div>
          <Button onClick={() => setInviteDialogOpen(true)} className="gap-2" data-testid="invite-member-btn">
            <UserPlus className="w-4 h-4" />
            Invite Member
          </Button>
        </div>

        {/* Team Members */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Team Members
            </CardTitle>
            <CardDescription>
              People who have access to this business
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Role legend */}
            <div className="mb-4 p-3 rounded-md bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span className="font-semibold text-slate-700 dark:text-slate-200">What the roles mean:</span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-purple-500 mr-1 align-middle" />
                <strong>Primary</strong> · main account holder for this business (full control here only)
              </span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1 align-middle" />
                <strong>Admin</strong> · invite/remove team, edit settings
              </span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1 align-middle" />
                <strong>Editor</strong> · edit invoices, bills, etc
              </span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-slate-400 mr-1 align-middle" />
                <strong>Viewer</strong> · read-only
              </span>
              <span className="basis-full text-[11px] text-slate-500 mt-1">
                Roles apply only within <strong>this business</strong>. Members cannot see any other client's data.
              </span>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No team members yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {member.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        {member.role === 'owner' ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[member.role]}`} title="Primary account holder of this business. Has full control within this workspace only — does NOT have access to other clients' data.">
                            <Shield className="w-3 h-3" />
                            Primary
                          </span>
                        ) : (
                          <Select
                            value={member.role}
                            onValueChange={(value) => handleUpdateRole(member.id, value)}
                          >
                            <SelectTrigger className="w-28 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="viewer">Viewer</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        {member.status === 'active' ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-600 text-sm">
                            <Clock className="w-4 h-4" />
                            Pending
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {member.joined_at 
                          ? new Date(member.joined_at).toLocaleDateString()
                          : '-'
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        {member.role !== 'owner' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(member.id, member.email)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Invite Dialog */}
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                Invite Team Member
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  placeholder="partner@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  data-testid="invite-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">
                      <div className="flex flex-col">
                        <span>Viewer</span>
                        <span className="text-xs text-slate-500">{ROLE_DESCRIPTIONS.viewer}</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="editor">
                      <div className="flex flex-col">
                        <span>Editor</span>
                        <span className="text-xs text-slate-500">{ROLE_DESCRIPTIONS.editor}</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="admin">
                      <div className="flex flex-col">
                        <span>Admin</span>
                        <span className="text-xs text-slate-500">{ROLE_DESCRIPTIONS.admin}</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {lastMagicLink && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 space-y-2 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Copy & Share This Link</span>
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    Please copy this invitation link and send it to <strong>{inviteEmail || 'your team member'}</strong> via email or message. They will need to create an account to join your team.
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      value={lastMagicLink}
                      readOnly
                      className="text-xs bg-white dark:bg-slate-800"
                    />
                    <Button size="sm" variant="outline" onClick={copyMagicLink} className="gap-1">
                      <Copy className="w-4 h-4" />
                      Copy
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setInviteDialogOpen(false); setLastMagicLink(null); setInviteEmail(''); }}>
                {lastMagicLink ? 'Done' : 'Cancel'}
              </Button>
              {!lastMagicLink && (
                <Button onClick={handleInvite} disabled={inviting} className="gap-2">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Create Invitation Link
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
